import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import {
  Badge,
  Button,
  Card,
  SectionHeader,
  Select,
  Skeleton,
  useActionDialog,
} from '../components/ui';
import {
  useAdminNotifications,
  useDeleteAdminNotification,
  useMarkNotificationRead,
  useMarkNotificationUnread,
} from '../features/notifications/hooks/useAdminNotifications';
import type { AdminNotificationCategory } from '../types/admin';
import styles from './NotificationsPage.module.scss';

const CATEGORY_OPTIONS: readonly AdminNotificationCategory[] = [
  'patient_registered',
  'payment_submitted',
  'payment_overdue',
  'chat_unread',
  'final_result_submitted',
];

const PAGE_SIZE = 20;

function formatDateTime(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { confirmAction } = useActionDialog();

  const [categoryFilter, setCategoryFilter] = useState<AdminNotificationCategory | ''>('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({
      ...(categoryFilter ? { category: categoryFilter } : {}),
      ...(unreadOnly ? { unread_only: true } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [categoryFilter, unreadOnly, page],
  );

  const { data, isLoading, isError, error } = useAdminNotifications(filters);
  const markRead = useMarkNotificationRead();
  const markUnread = useMarkNotificationUnread();
  const deleteMutation = useDeleteAdminNotification();

  const busy = markRead.isPending || markUnread.isPending || deleteMutation.isPending;

  function handleMarkRead(id: string) {
    markRead.mutate(id);
  }

  function handleMarkUnread(id: string) {
    markUnread.mutate(id);
  }

  async function handleDelete(id: string) {
    if (!(await confirmAction({
      message: t('notifications.confirmDelete'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) return;
    deleteMutation.mutate(id);
  }

  if (isLoading) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('notifications.pageTitle')} />
          <div className={styles.skeletonList}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height={88} />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('notifications.pageTitle')} />
          <EmptyState
            title={t('notifications.loadErrorTitle')}
            description={
              error instanceof Error ? error.message : t('notifications.loadErrorDescription')
            }
          />
        </div>
      </AppShell>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader title={t('notifications.pageTitle')} />

        <Card padding="md">
          <div className={styles.filters}>
            <Select
              label={t('notifications.filterCategory')}
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value as AdminNotificationCategory | '');
                setPage(1);
              }}
            >
              <option value="">{t('notifications.filterAny')}</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {t(`notifications.categoryLabels.${c}`)}
                </option>
              ))}
            </Select>
            <label className={styles.unreadOnly}>
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => {
                  setUnreadOnly(e.target.checked);
                  setPage(1);
                }}
              />
              {t('notifications.unreadOnly')}
            </label>
          </div>
        </Card>

        {items.length === 0 ? (
          <EmptyState title={t('notifications.empty')} />
        ) : (
          <div className={styles.list}>
            {items.map((notification) => {
              const isUnread = notification.read_at == null;
              const titleClass = `${styles.title} ${isUnread ? styles.titleUnread : ''}`.trim();
              return (
                <Card key={notification.id} padding="md">
                  <div className={styles.row}>
                    <div className={styles.identity}>
                      <div className={styles.titleLine}>
                        <span className={titleClass}>{notification.title}</span>
                        <Badge variant="neutral" size="sm">
                          {t(`notifications.categoryLabels.${notification.category}`)}
                        </Badge>
                        {isUnread ? (
                          <Badge variant="info" size="sm">
                            {t('notifications.unread')}
                          </Badge>
                        ) : null}
                      </div>
                      {notification.body ? (
                        <p className={styles.body}>{notification.body}</p>
                      ) : null}
                      {notification.category === 'final_result_submitted' && notification.related_patient_id && <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${notification.related_patient_id}${notification.related_cycle_id ? `?result_cycle=${encodeURIComponent(notification.related_cycle_id)}` : ''}#result`)}>{i18n.language.startsWith('en') ? 'View result' : 'Переглянути результат'}</Button>}
                      <div className={styles.meta}>
                        <span>{formatDateTime(notification.created_at, i18n.language)}</span>
                      </div>
                    </div>
                    <div className={styles.rowActions}>
                      {isUnread ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkRead(notification.id)}
                          disabled={busy}
                        >
                          {t('notifications.markRead')}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkUnread(notification.id)}
                          disabled={busy}
                        >
                          {t('notifications.markUnread')}
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(notification.id)}
                        disabled={busy}
                      >
                        {t('notifications.delete')}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {total > PAGE_SIZE ? (
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || busy}
            >
              {t('notifications.previous')}
            </Button>
            <span style={{ padding: '0 12px', alignSelf: 'center' }}>
              {page} / {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || busy}
            >
              {t('notifications.next')}
            </Button>
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
